<template>
    <CampaignBanner
        class="qa-banner"
        :class="{ 'qa-banner--selected': isSelected }"
        :master-template-height="displayDimensions.height"
        :master-template-width="displayDimensions.width"
        :max-height="maxHeight"
        :max-width="maxWidth"
        :scaling-factor="scalingFactor"
        @click="selectBanner"
        @scalingToggle="onScalingToggle"
        @updateInternalScalingFactor="internalScalingFactor = $event"
    >
        <template #bannerContainer="">
            <div class="qa-banner__banner-container">
                <div class="qa-banner__select-layer" />

                <div
                    v-if="isSelected && userCanAddAnnotations"
                    class="qa-banner__add-annotation-layer"
                    @mouseover="isHovered = true"
                    @mouseleave="isHovered = false"
                    @click="showAddNewAnnotation($event, internalScalingFactor)"
                >
                    <QaBannerAddAnnotation
                        :visible="newAnnotationIsVisible"
                        :deliverable="deliverable"
                        :position="newAnnotationMetadata.position"
                        :scaling-factor="internalScalingFactor"
                        :timeline="newAnnotationMetadata.timeline"
                        @annotationAdded="annotationAdded"
                        @close="closeAddNewAnnotation"
                    />

                    <QaBannerCursorInfo v-if="!newAnnotationIsVisible && isHovered" />
                </div>

                <QaBannerAnnotations
                    v-if="isSelected && annotations.length > 0"
                    :annotations="annotations"
                    :selected-annotation="selectedAnnotation"
                    :scaling-factor="internalScalingFactor"
                    :show-resolved="showResolved"
                    @annotationSelected="$emit('annotationSelected', $event)"
                />
            </div>

            <BannerPreview
                :id="bannerId"
                :preview-values="previewValues(deliverable)"
                :ad-type="deliverable.masterTemplate.adType"
                :scaling-factor="internalScalingFactor"
                :playback-namespace="playbackNamespace"
            />
        </template>

        <template #bannerFooter>
            <div class="qa-banner__footer-items">
                <BannerFooterStatus
                    v-if="$auth.userCan($auth.Actions.CanChangeDeliverableQAStatus, { clientId, campaignId })"
                    :deliverable="deliverable"
                    @statusHasBeenUpdated="$emit('statusHasBeenUpdated')"
                />

                <BannerFooterStatusIndicator v-else :deliverable="deliverable" />
            </div>

            <div class="qa-banner__title">
                <BannerFooterInfoPoptip :deliverable="deliverable" />
            </div>

            <div class="qa-banner__approval-buttons-container">
                <Tooltip class="qa-banner__approval-buttons-tooltip" content="Approve this banner" placement="bottom">
                    <square-button
                        :is-loading="isApprovingBanner"
                        :is-solid="usersDeliverableStatus === BannerQaStatus.Approved"
                        type="success"
                        @click="setDeliverableUserStatusToApproved"
                    >
                        <Icon :size="approvalIconSize" type="ios-checkmark" />
                    </square-button>
                </Tooltip>

                <Tooltip class="qa-banner__approval-buttons-tooltip" content="Reject this banner" placement="bottom">
                    <square-button
                        :is-loading="isRejectingBanner"
                        :is-solid="usersDeliverableStatus === BannerQaStatus.Rejected"
                        type="danger"
                        @click="setDeliverableUserStatusToRejected"
                    >
                        <Icon :size="approvalIconSize" type="ios-close" />
                    </square-button>
                </Tooltip>
            </div>
        </template>
    </CampaignBanner>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import type { ConstructedDeliverable, QaAnnotation } from "@/types/types";
import bus from "@/bus";
import BannerPreview from "@/components/Previews/BannerPreview.vue";
import SquareButton from "@/components/common/SquareButton.vue";

import QaBannerAddAnnotation from "@/components/Qa/QaBannerAddAnnotation.vue";
import QaBannerAnnotations from "@/components/Qa/QaBannerAnnotations.vue";
import CampaignBanner from "@/components/Campaign/CampaignBanner.vue";
import QaBannerCursorInfo from "@/components/Qa/QaBannerCursorInfo.vue";

import BannerFooterInfoPoptip from "@/components/Previews/BannerFooter/BannerFooterInfoPoptip.vue";
import BannerFooterStatusIndicator from "@/components/Previews/BannerFooter/BannerFooterStatusIndicator.vue";
import BannerFooterStatus from "@/components/Previews/BannerFooter/BannerFooterStatus.vue";

import { BannerQaStatus } from "@/enums/banners";
import { setLoggedInUsersDeliverablesStatus } from "@/services/Qa";
import { DeliverableLibraryAction, DeliverableLibraryGetters } from "@/store/modules/deliverableLibrary";
import { getBannerIdFromDeliverable } from "@/utils/getBannerIframeId";
import { getTemplateDisplayDimensions } from "@/utils";
import { PlaybackBannerActions, PlaybackBannerEvents } from "@/components/PlaybackControls/usePlaybackController";
import { usePreviewValues } from "@/composables/usePreviewValues";

import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

export default defineComponent({
    components: {
        CampaignBanner,
        QaBannerCursorInfo,
        BannerFooterInfoPoptip,
        BannerPreview,
        BannerFooterStatusIndicator,
        QaBannerAddAnnotation,
        QaBannerAnnotations,
        BannerFooterStatus,
        SquareButton
    },

    props: {
        showResolved: {
            type: Boolean,
            default: true
        },

        deliverable: {
            type: Object as PropType<ConstructedDeliverable>,
            required: true
        },

        externalAnnotations: {
            type: Array as PropType<QaAnnotation[]>,
            required: true
        },

        internalAnnotations: {
            type: Array as PropType<QaAnnotation[]>,
            required: true
        },

        isSelected: {
            type: Boolean
        },

        selectedAnnotation: {
            type: [Object, null] as PropType<QaAnnotation | null>
        },

        maxHeight: {
            type: Number,
            required: true
        },

        maxWidth: {
            type: Number,
            required: true
        },

        scalingFactor: {
            type: Number,
            required: true
        },

        playbackNamespace: {
            type: String,
            required: true
        }
    },

    emits: ["annotationAdded", "annotationSelected", "select", "userQaStatusHasBeenUpdated", "statusHasBeenUpdated"],

    setup() {
        const previewValues = usePreviewValues();
        const ga4 = useGA4Tracking();

        return {
            previewValues,
            ga4
        };
    },

    data() {
        const newAnnotationMetadata = {
            timeline: undefined,
            position: undefined
        } as {
            timeline: number | undefined;
            position: { x: number; y: number } | undefined;
        };

        return {
            currentBannerTime: 0,

            approvalIconSize: 38,
            isApprovingBanner: false,
            isHovered: false,
            isRejectingBanner: false,
            newAnnotationMetadata,
            newAnnotationIsVisible: false,
            BannerQaStatus,

            internalScalingFactor: this.scalingFactor
        };
    },

    computed: {
        displayDimensions() {
            return getTemplateDisplayDimensions(this.deliverable.masterTemplate);
        },

        annotations() {
            return [...this.externalAnnotations, ...this.internalAnnotations];
        },

        bannerId() {
            return getBannerIdFromDeliverable(this.deliverable, this.playbackNamespace);
        },

        campaignId() {
            return this.$route.params.campaignId as string;
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        userCanAddAnnotations() {
            return (
                this.$auth.userCan(this.$auth.Actions.CanManageExternalAnnotations, {
                    clientId: this.clientId,
                    campaignId: this.campaignId
                }) ||
                this.$auth.userCan(this.$auth.Actions.CanManageInternalAnnotations, {
                    clientId: this.clientId,
                    campaignId: this.campaignId
                })
            );
        },

        usersDeliverableStatus() {
            return this.$store.getters[DeliverableLibraryGetters.getLoggedInUserStatusForIdHash](
                this.deliverable.idHash
            );
        }
    },
    watch: {
        isSelected() {
            if (!this.isSelected) {
                this.closeAddNewAnnotation();
            }
        }
    },

    mounted() {
        bus.$on(PlaybackBannerEvents.SetBannerTime, ({ namespace, id, currentTime: time }) => {
            if (namespace === this.playbackNamespace && id === this.bannerId) {
                this.currentBannerTime = time;
            }
        });
    },

    methods: {
        annotationAdded(annotation: QaAnnotation) {
            this.$emit("annotationAdded", annotation);
            this.closeAddNewAnnotation();
        },

        async showAddNewAnnotation(evt: MouseEvent, scalingFactor: number) {
            evt.stopPropagation();
            evt.preventDefault();

            this.ga4.trackFeatureEvent({
                featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
                featureCategory: GA4.FEATURE_CATEGORIES.QA_DRAWER,
                featureContext: GA4.FEATURE_CONTEXTS.ADD_NEW_ANNOTATION
            });

            const bounds = this.$el.getBoundingClientRect();
            const manualOffset = {
                x: 2,
                y: 38
            };

            this.newAnnotationMetadata.position = {
                x: Math.floor((evt.clientX - bounds.left - manualOffset.x) / scalingFactor),
                y: Math.floor((evt.clientY - bounds.top - manualOffset.y) / scalingFactor)
            };

            this.newAnnotationMetadata.timeline = this.currentBannerTime;
            this.newAnnotationIsVisible = true;

            bus.$emit(PlaybackBannerActions.Pause, { namespace: this.playbackNamespace });
        },

        closeAddNewAnnotation() {
            this.newAnnotationIsVisible = false;

            this.newAnnotationMetadata = {
                timeline: undefined,
                position: undefined
            };
        },

        onScalingToggle(isEnabled: boolean) {
            this.newAnnotationIsVisible = !(!isEnabled && this.newAnnotationIsVisible);
        },

        selectBanner(event: MouseEvent) {
            event.stopPropagation();
            event.preventDefault();

            this.$emit("select");
        },

        async setDeliverableUserStatus(status: (typeof BannerQaStatus)[keyof typeof BannerQaStatus]) {
            let statusToSet = status;
            if (status === this.usersDeliverableStatus) {
                /*
                    If status is equal to what we have for the user and deliverable
                    then we want to unset the users approval/rejection: we do this
                    by giving it the draft state.
                */
                statusToSet = BannerQaStatus.Draft;
            }
            const deliverables = await setLoggedInUsersDeliverablesStatus(
                this.campaignId,
                [this.deliverable],
                statusToSet
            );
            this.$store.dispatch(DeliverableLibraryAction.SetDeliverableStatusByIdHash, deliverables);
        },

        async setDeliverableUserStatusToApproved() {
            this.isApprovingBanner = true;

            try {
                await this.setDeliverableUserStatus(BannerQaStatus.Approved);

                if (this.usersDeliverableStatus === BannerQaStatus.Approved) {
                    this.$snackbar.success("Deliverable approved by you.");
                } else {
                    this.$snackbar.success("The deliverable is no longer approved by you.");
                }
                this.ga4.trackClickEvent({
                    interactionObject: GA4.CLICK_INTERACTION_OBJECTS.BUTTON,
                    interactionText: GA4.CLICK_INTERACTION_TEXTS.APPROVE_BANNER
                });

                this.$emit("userQaStatusHasBeenUpdated");
            } catch {
                this.$snackbar.error("Sorry, We could not change your ads’ status this time. Try again in a moment.");
            }

            this.isApprovingBanner = false;
        },

        async setDeliverableUserStatusToRejected() {
            this.isRejectingBanner = true;

            try {
                await this.setDeliverableUserStatus(BannerQaStatus.Rejected);

                if (this.usersDeliverableStatus === BannerQaStatus.Rejected) {
                    this.$snackbar.success("Deliverable rejected by you.");
                } else {
                    this.$snackbar.success("The deliverable is no longer rejected by you.");
                }

                this.ga4.trackClickEvent({
                    interactionObject: GA4.CLICK_INTERACTION_OBJECTS.BUTTON,
                    interactionText: GA4.CLICK_INTERACTION_TEXTS.REJECT_BANNER
                });

                this.$emit("userQaStatusHasBeenUpdated");
            } catch {
                this.$snackbar.error("Sorry, We could not change your ads’ status this time. Try again in a moment.");
            }

            this.isRejectingBanner = false;
        }
    }
});
</script>

<style lang="scss">
@use "sass:color";
@use "@style/variables" as *;

.qa-banner {
    border: 1px solid $grey3;

    transition:
        border-color 0.2s linear,
        box-shadow 0.2s linear;
}

.qa-banner--selected {
    border-color: $darkgrey1;
    box-shadow: 3px 5px 8px 3px color.adjust($darkgrey1, $lightness: 15%);

    .qa-banner__select-layer {
        cursor: default;
    }
}

.qa-banner__banner-container {
    position: absolute;

    width: 100%;
    height: 100%;
}

.qa-banner__approval-buttons-tooltip {
    height: 40px;
}

.qa-banner__approval-buttons-container {
    display: flex;
}

.qa-banner__footer {
    justify-content: space-between;
}

.qa-banner__footer-items {
    display: flex;
}

.qa-banner__add-annotation-layer {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;

    &:hover .qa-banner-cursor-info {
        display: block;
    }
}

.qa-banner__select-layer {
    bottom: 0;
    cursor: pointer;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
}

.qa-banner__title {
    color: $darktheme20;
    flex: 1 0;
    font-size: $font-size-small;
    min-width: 0;
}
</style>
